<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import { useMultiSelectFilter } from '@/composables/data-grid'
import { useColumns } from '@/composables/registry/mechanic-grid'

import { useWebSiteStore } from '@/stores/web-site.store'

import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import { useEntityLock } from '@/services/entity-lock.service'
import { webSiteApi } from '@/apis/web-site.api'

const gridId = 'mechanic-grid'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()

const { gridRef, refreshGridAsync } = dataGrid.useGridRef()
const formInstance = ref(null)
const isReadOnly = ref(false)
const showDeleted = ref(false)

const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()

const buildFilter = () => {
  const baseFilter = ['is_mechanic', '=', true]

  if (!showDeleted.value) {
    return [
      baseFilter,
      'and',
      [
        ['deleted_at', '=', null],
        'or',
        ['deleted_at', '>', new Date()]
      ]
    ]
  }

  return baseFilter
}

const gridDataSource = dataGrid.useODataSource(
  webSiteStore.api.getAbsoluteUrl() + webSiteStore.api.userDetailsUrl,
  {
    filter: buildFilter()
  }
)

const alreadyDeleted = (data) => {
  return data?.deleted_at && new Date(data.deleted_at).getTime() <= Date.now()
}

const entityLock = useEntityLock({
  baseLockUrl: webSiteStore.api.getAbsoluteUrl() + webSiteStore.api.userDetailsUrl,
  modelName: 'UserDetail',
  gridRef,
})
const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => (!e.data?.deleted_at || new Date(e.data.deleted_at).getTime() > Date.now()) && !e.isReadonly && !entityLock.isLocked(false, e.entityId),
    delete: (e) => (!e.data?.deleted_at || new Date(e.data.deleted_at).getTime() > Date.now()) && !e.isReadonly && !entityLock.isLocked(false, e.entityId),
    view: (e) => alreadyDeleted(e.data) || e.isReadonly || entityLock.isLocked(false, e.entityId)
  }
})
rowButtons.push(
  {
    name: 'restore',
    text: i18n.t('common.restore'),
    icon: 'mdi-restore',
    class: ['dx-link', 'dx-icon', 'mdi', 'mdi-restore', 'dx-link-icon'],
    onClick: async(e) => {
      webSiteApi.restoreDeletedUserDetail(e.entityId).then(async() => {
        await refreshGridAsync()
      })
    },
    visibleFn: (e) => {
      return alreadyDeleted(e.data)
    }
  }
)

const onInitializedCustom = (e) => {
  if (isReadOnly.value) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: !isReadOnly.value,
      }
    })

    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const columns = useColumns()

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.mechanic')), {
  colCount: 1,
  items: [
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 2,
          items: [
            { dataField: 'name' },
            { dataField: 'name_ext' },
            { dataField: 'color', editorType: 'dxColorBox' },
            { dataField: 'branch'},
            { dataField: 'employment_start_date' },
            { dataField: 'deleted_at' },
          ]
        },
      ]
    },

  ],
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  allowUpdating: (e) => e.data?.deleted_at && !entityLock.isLocked(false, e.entityId),
  allowDeleting: (e) => e.data?.deleted_at && !entityLock.isLocked(false, e.entityId),
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: entityLock,
  width: '50rem'
})

const onClearFilter = () => {
  clearMultiSelectFilter()
}

const onInitNewRow = (e) => {
  e.data.is_mechanic = true
}

const onRowInserted = async() => {
  await webSiteStore.fetchUserDetailsAsync(true)
}

const onEditingStart = (e) => {
  if (alreadyDeleted(e.data)) {
    isReadOnly.value = true
    return
  }
}

const onEditorPreparing = (e) => {
  if (isReadOnly.value && e.parentType === 'dataRow') {
    e.editorOptions.readOnly = true
  }
}

const onEditCanceled = (e) => {
  isReadOnly.value = false
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data' && alreadyDeleted(e.data)) { e.rowElement.classList.add('dx-deleted-entry') }
}

const onSaved = async(e) => {
  await webSiteStore.fetchUserDetailsAsync(true)
  await refreshGridAsync()
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      location: 'after',
      widget: 'dxSwitch',
      options: {
        hint: i18n.t('common.showDeleted'),
        value: showDeleted.value,
        onValueChanged: (e) => {
          showDeleted.value = e.value
          gridDataSource.filter(buildFilter())
          gridDataSource.load()
        }
      }
    }
  )

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  onClearFilter: onClearFilter,
  onInitNewRow: onInitNewRow,
  onRowInserted: onRowInserted,
  onSaved: onSaved,
  onEditCanceled: onEditCanceled,
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onRowPrepared: onRowPrepared,
  onToolbarPreparing: onToolbarPreparing,
  height: '90vh',
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :gridExtRef="gridRef"
          :gridExtId="gridId"
        />
      </template>
      <template #colorCellTemplate="{ data }">
        <div style="display: flex; align-items: center; gap: 8px;">
          <div
            :style="{
              width: '16px',
              height: '16px',
              backgroundColor: data.data.color,
              border: '1px solid #ccc',
            }"
          />
          <span>{{ data.data.color }}</span>
        </div>
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div class="dx-form-group-caption-wrapper">
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>

      <template #multiSelectFilterRef="{ data }">
        <multi-select-filter
          :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
          :field="data.component.option('field')"
          :grid-ref="gridRef"
          :apply-filter="applyFilter"
          v-bind="{ data }"
        />
      </template>

      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Mechanic'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>

<style>

.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
